import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useDataMode } from '../../contexts/DataModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useDropdown, useCities, PhoneField } from '../../contexts/DropdownContext';
import { Save, Building2, User, Layers, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOCK_PROFILE = {
  org: { display_name: 'Global Exhibitions Inc.', country: 'Türkiye', city: 'İstanbul', website: 'https://globalexpo.com', status: 'active' },
  profile: {
    activity: { industries: ['Technology', 'Manufacturing'], typical_booth: { w_m: 6, d_m: 4 }, shows_per_year: 8, annual_budget_min: 250000, annual_budget_max: 500000, currency: 'TRY' },
    primary_contact: { name: 'Ahmet Yilmaz', email: 'ahmet@globalexpo.com', phone_e164: '+905321234567' }
  },
  plan_code: 'professional'
};

const PLAN_COLORS = { basic: 'bg-gray-100 text-gray-700', professional: 'bg-blue-100 text-blue-700', enterprise: 'bg-purple-100 text-purple-700' };

const FALLBACK_CURRENCIES = ['TRY','USD','EUR','GBP','AED'];

const ExhibitorCompanyProfile = () => {
  const { isMockMode } = useDataMode();
  const { token } = useAuth();
  const { t } = useTranslation();
  const { countries, phoneCodes, currencies: ctxCurrencies } = useDropdown();
  const [countryCode, setCountryCode] = useState('');
  const [phoneField, setPhoneField] = useState({ code: '+90', number: '' });
  const { cities } = useCities(countryCode);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [planCode, setPlanCode] = useState('');

  const [form, setForm] = useState({
    display_name: '', country: '', city: '', website: '',
    contact_name: '', contact_email: '', contact_phone: '',
    industries: '', shows_per_year: '', booth_w: '', booth_d: '',
    budget_min: '', budget_max: '', currency: 'TRY',
  });

  // Deduplicate currencies from context
  const displayCurrencies = useMemo(() => {
    if (ctxCurrencies.length > 0) {
      const seen = new Set();
      return ctxCurrencies.filter(c => {
        if (!c.currency_code || seen.has(c.currency_code)) return false;
        seen.add(c.currency_code);
        return true;
      });
    }
    return null;
  }, [ctxCurrencies]);

  // When countries loads OR form.country changes, map stored country name → ISO code
  useEffect(() => {
    if (countries.length > 0 && form.country) {
      const isCode = countries.some(c => c.code === form.country);
      if (!isCode) {
        const found = countries.find(c => c.name.toLowerCase() === form.country.toLowerCase());
        if (found) { setForm(p => ({ ...p, country: found.code })); setCountryCode(found.code); }
      } else {
        setCountryCode(form.country);
      }
    }
  }, [countries, form.country]); // eslint-disable-line

  // When phoneCodes loads OR form.contact_phone changes, properly parse phone_e164 using known codes
  useEffect(() => {
    if (phoneCodes.length > 0 && form.contact_phone) {
      const sorted = [...phoneCodes].sort((a, b) => b.phone_code.length - a.phone_code.length);
      for (const pc of sorted) {
        if (form.contact_phone.startsWith(pc.phone_code)) {
          setPhoneField({ code: pc.phone_code, number: form.contact_phone.slice(pc.phone_code.length) });
          return;
        }
      }
      setPhoneField({ code: '+90', number: form.contact_phone.replace(/[^\d]/g, '').slice(0, 12) });
    }
  }, [phoneCodes, form.contact_phone]); // eslint-disable-line

  useEffect(() => {
    const load = async () => {
      setLoading(true); setError(null);
      let data;
      if (isMockMode) {
        await new Promise(r => setTimeout(r, 500));
        data = MOCK_PROFILE;
      } else {
        try {
          const res = await axios.get(`${API}/exhibitor/profile`, { headers: { Authorization: `Bearer ${token}` } });
          data = res.data;
        } catch { setError('Could not load profile.'); setLoading(false); return; }
      }
      setPlanCode(data.plan_code || '');
      setForm({
        display_name: data.org?.display_name || '',
        country: data.org?.country || '',
        city: data.org?.city || '',
        website: data.org?.website || '',
        contact_name: data.profile?.primary_contact?.name || '',
        contact_email: data.profile?.primary_contact?.email || '',
        contact_phone: data.profile?.primary_contact?.phone_e164 || '',
        industries: (data.profile?.activity?.industries || []).join(', '),
        shows_per_year: String(data.profile?.activity?.shows_per_year || ''),
        booth_w: String(data.profile?.activity?.typical_booth?.w_m || ''),
        booth_d: String(data.profile?.activity?.typical_booth?.d_m || ''),
        budget_min: String(data.profile?.activity?.annual_budget_min || ''),
        budget_max: String(data.profile?.activity?.annual_budget_max || ''),
        currency: data.profile?.activity?.currency || 'TRY',
      });
      setLoading(false);
    };
    load();
  }, [isMockMode, token]);

  const handleSave = async () => {
    if (!form.contact_name?.trim() && !form.contact_email?.trim()) {
      setError(t('validation.required') + ' — Yetkili adı veya e-posta gerekli');
      return;
    }
    if (form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) {
      setError(t('validation.invalidEmail'));
      return;
    }
    setSaving(true);
    if (!isMockMode) {
      try {
        await axios.patch(`${API}/exhibitor/profile`, {
          activity: {
            industries: form.industries.split(',').map(s => s.trim()).filter(Boolean),
            typical_booth: { w_m: parseFloat(form.booth_w) || 3, d_m: parseFloat(form.booth_d) || 3 },
            shows_per_year: parseInt(form.shows_per_year) || 0,
            annual_budget_min: parseFloat(form.budget_min) || 0,
            annual_budget_max: parseFloat(form.budget_max) || 0,
            currency: form.currency,
          },
          primary_contact: { name: form.contact_name, email: form.contact_email, phone_e164: phoneField.code + phoneField.number }
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch { setError('Save failed.'); setSaving(false); return; }
    }
    await new Promise(r => setTimeout(r, 600));
    setSaved(true); setTimeout(() => setSaved(false), 2500);
    setSaving(false);
  };

  const Field = ({ label, value, onChange, type = 'text', readOnly = false }) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <Input type={type} value={value} onChange={onChange ? e => onChange(e.target.value) : undefined} readOnly={readOnly}
        className={`bg-gray-50 border-gray-200 ${readOnly ? 'opacity-60 cursor-not-allowed' : 'focus:ring-blue-500'}`} />
    </div>
  );

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_,i)=><div key={i} className="h-32 bg-gray-100 animate-pulse rounded-xl"/>)}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your company details and exhibition profile</p>
        </div>
        <div className="flex items-center gap-3">
          {planCode && <Badge className={PLAN_COLORS[planCode] || 'bg-gray-100 text-gray-700'}>{planCode.charAt(0).toUpperCase() + planCode.slice(1)} Plan</Badge>}
          <Button onClick={handleSave} disabled={saving} className={`gap-2 ${saved ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-orange-500 hover:bg-orange-600'} text-white`}>
            {saved ? <><CheckCircle2 className="h-4 w-4" />Saved</> : <><Save className="h-4 w-4" />{saving ? 'Saving...' : 'Save Changes'}</>}
          </Button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3"><AlertCircle className="h-5 w-5 text-red-500"/><p className="text-sm text-red-700">{error}</p></div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50"><CardTitle className="text-sm flex items-center gap-2"><Building2 className="h-4 w-4 text-orange-500"/>Company Information</CardTitle></CardHeader>
          <CardContent className="p-5 space-y-4">
            <Field label="Company Name" value={form.display_name} onChange={v=>setForm(p=>({...p,display_name:v}))}/>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Country</label>
              <select
                value={form.country}
                onChange={e => { setForm(p=>({...p,country:e.target.value,city:''})); setCountryCode(e.target.value); }}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select country</option>
                {countries.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
              {cities.length > 0 ? (
                <select
                  value={form.city}
                  onChange={e => setForm(p=>({...p,city:e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select city</option>
                  {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              ) : (
                <Input type="text" value={form.city} onChange={e=>setForm(p=>({...p,city:e.target.value}))}
                  placeholder="City" className="bg-gray-50 border-gray-200 focus:ring-blue-500" />
              )}
            </div>
            <Field label="Website" value={form.website} onChange={v=>setForm(p=>({...p,website:v}))}/>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50"><CardTitle className="text-sm flex items-center gap-2"><User className="h-4 w-4 text-orange-500"/>Primary Contact</CardTitle></CardHeader>
          <CardContent className="p-5 space-y-4">
            <Field label="Full Name" value={form.contact_name} onChange={v=>setForm(p=>({...p,contact_name:v}))}/>
            <Field label="Email" value={form.contact_email} onChange={v=>setForm(p=>({...p,contact_email:v}))}/>
            <PhoneField
              value={phoneField}
              onChange={setPhoneField}
              label="Phone"
            />
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader className="pb-3 border-b border-gray-50"><CardTitle className="text-sm flex items-center gap-2"><Layers className="h-4 w-4 text-orange-500"/>Exhibition Activity</CardTitle></CardHeader>
          <CardContent className="p-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Field label="Industries (comma-separated)" value={form.industries} onChange={v=>setForm(p=>({...p,industries:v}))}/>
              <Field label="Shows per Year" value={form.shows_per_year} onChange={v=>setForm(p=>({...p,shows_per_year:v}))} type="number"/>
              <Field label="Booth Width (m)" value={form.booth_w} onChange={v=>setForm(p=>({...p,booth_w:v}))} type="number"/>
              <Field label="Booth Depth (m)" value={form.booth_d} onChange={v=>setForm(p=>({...p,booth_d:v}))} type="number"/>
              <Field label="Min Budget" value={form.budget_min} onChange={v=>setForm(p=>({...p,budget_min:v}))} type="number"/>
              <Field label="Max Budget" value={form.budget_max} onChange={v=>setForm(p=>({...p,budget_max:v}))} type="number"/>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Currency</label>
                <select value={form.currency} onChange={e=>setForm(p=>({...p,currency:e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {(displayCurrencies || FALLBACK_CURRENCIES.map(c => ({ currency_code: c }))).map(c => (
                    <option key={c.currency_code} value={c.currency_code}>
                      {c.currency_symbol ? `${c.currency_symbol} ` : ''}{c.currency_code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExhibitorCompanyProfile;
